'use client';
import buildRecipeToPrint from '@functions/print/recipePrint';
import useTranslation from '@repo/ui/src/context/Translation/useTranslation';
import {
  IngredientSummaryProps,
  RecipeStepProps,
  RecipeMetaSectionProps,
} from '@repo/ui/src/types';
import { SegmentRecipeProps } from '@components/RecipeNotes/_RecipeNote';
import { recipePrinted } from '@repo/ui/src/services/analytics';
import Button, { ButtonVariant } from '@repo/ui/src/components/Button';
import PrintIcon from '@repo/ui/src/components/Icons/PrintIcon';
import { RecipeTimeProps } from '@views/RecipeDetailPage/_RecipeHero';

interface PrintProps {
  recipeMetaSection: RecipeMetaSectionProps;
  ingredientSummary?: IngredientSummaryProps;
  authorName: string;
  servingSize: number;
  prepTime: Array<RecipeTimeProps>;
  cookTime: Array<RecipeTimeProps>;
  unitOfMeasure: number;
  steps?: Array<RecipeStepProps>;
  segmentRecipe?: SegmentRecipeProps;
  variant?: 'icon' | 'button';
}

const printRecipe = ({
  recipeMetaSection,
  ingredientSummary,
  prepTime,
  cookTime,
  authorName,
  steps,
  servingSize,
  unitOfMeasure,
}: PrintProps) => {
  try {
    const res = buildRecipeToPrint({
      recipeMetaSection,
      ingredientSummary,
      prepTime,
      cookTime,
      authorName,
      steps,
      servingSize,
      unitOfMeasure,
    });
    if (res) {
      const wnd = window.open();
      if (wnd) {
        wnd.document.write(res);
        wnd.print();
      }
    }
  } catch (error) {
    console.error(error);
  }
};

const PrintButton = ({
  recipeMetaSection,
  servingSize,
  authorName,
  ingredientSummary,
  unitOfMeasure,
  prepTime,
  cookTime,
  steps,
  segmentRecipe,
  variant = 'button',
}: PrintProps) => {
  const { t } = useTranslation();
  const handlePrint = () => {
    printRecipe({
      recipeMetaSection,
      ingredientSummary,
      prepTime,
      cookTime,
      authorName,
      steps,
      servingSize,
      unitOfMeasure,
      segmentRecipe,
    });
    recipePrinted({
      ...segmentRecipe,
      pellet_recommendation: segmentRecipe?.pellet_recommendation ?? null,
      recipe_id: '',
      recipe_author_name: segmentRecipe?.recipe_author_name ?? null,
      recipe_name: segmentRecipe?.recipe_name ?? '',
      recipe_url: segmentRecipe?.recipe_url ?? '',
      recipe_image_url: segmentRecipe?.recipe_image_url ?? null,
      recipe_meat_type: segmentRecipe?.recipe_type ?? null,
      recipe_type: segmentRecipe?.recipe_type ?? undefined,
      recipe_cook_time: segmentRecipe?.recipe_cook_time ?? null,
      recipe_measurement_units: segmentRecipe?.recipe_measurement_units ?? '',
      recipe_prep_time: segmentRecipe?.recipe_prep_time ?? null,
      recipe_serving_size: segmentRecipe?.recipe_serving_size ?? null,
    });
  };

  return variant === 'icon' ? (
    <div className="tw-relative tw-pt-5 md:tw-pt-0">
      <button
        className="tw-flex tw-justify-center tw-bg-warm-300 tw-rounded-full hover:tw-bg-brand-300 hover:tw-text-warm-100 focus:tw-text-brand-400"
        onClick={handlePrint}
      >
        <PrintIcon />
      </button>
    </div>
  ) : (
    <div className="tw-flex">
      <Button
        variant={ButtonVariant.secondary}
        onClick={handlePrint}
        className="tw-flex tw-justify-center tw-w-full tw-border-2 tw-items-center tw-py-2 sm:tw-px-5 lg:tw-px-10 tw-rounded"
      >
        <PrintIcon />
        <p className="tra-text-web-utl-button-m tw-mb-0 tw-ml-2">
          {t('recipeContent.printLabel', undefined, { default: 'Print Recipe' })}
        </p>
      </Button>
    </div>
  );
};

export default PrintButton;
